import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
  type SortDirection,
} from '@/components/ui/data-table';
import type { FamilySortKey } from '@/features/families/lib/sortFamilySummaries';
import { formatDate } from '@/lib/format';
import type { FamilySummaryViewModel } from '@/models/family';

/** Rows + header cells for the Browse Families list. */
export function BrowseFamiliesTableBody({
  families,
  emptyCell,
  sortKey,
  sortDir,
  onSortChange,
}: {
  readonly families: readonly FamilySummaryViewModel[];
  readonly emptyCell: string;
  readonly sortKey: FamilySortKey | null;
  readonly sortDir: SortDirection;
  readonly onSortChange: (key: FamilySortKey) => void;
}): ReactElement {
  const { t } = useTranslation();

  const sortProps = (
    key: FamilySortKey,
  ): { readonly sortDirection: SortDirection | null; readonly onSort: () => void } => ({
    sortDirection: sortKey === key ? sortDir : null,
    onSort: () => {
      onSortChange(key);
    },
  });

  return (
    <DataTableTable aria-label={t('routes.browseFamilies.listAriaLabel')}>
      <DataTableThead>
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[28%] min-w-[8rem]" sortable {...sortProps('name')}>
            {t('routes.browseFamilies.table.familyName')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[24%] min-w-[10rem]">
            {t('routes.browseFamilies.table.proband')}
          </DataTableTh>
          <DataTableTh className="w-24" align="right" sortable {...sortProps('members')}>
            {t('routes.browseFamilies.table.members')}
          </DataTableTh>
          <DataTableTh
            className="w-32"
            widthClassName="w-32"
            align="right"
            sortable
            {...sortProps('lastModified')}
          >
            {t('routes.browseFamilies.table.lastModified')}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {families.map((f) => (
          <DataTableTr key={f.guid}>
            <DataTableTd className="pr-1 font-medium text-foreground" dataOffset={false}>
              <Link
                to={`/families/${f.guid}`}
                className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {f.name.length > 0 ? f.name : t('routes.browseFamilies.unnamedFamily')}
              </Link>
            </DataTableTd>
            <DataTableTd className="text-foreground/90">
              {f.probandGuid !== null ? (
                f.probandIsPatient ? (
                  // Proband is a registered patient → the name links to its record.
                  <Link
                    to={`/patients/${f.probandGuid}`}
                    className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    {f.probandName ??
                      t('routes.browseFamilies.table.unnamedProband', { defaultValue: 'Unnamed proband' })}
                  </Link>
                ) : (
                  // Proband exists but is not a patient → no record to open; show name as plain text.
                  <span
                    title={t('routes.browseFamilies.table.probandNotPatient', {
                      defaultValue: 'Not a patient, no record to open',
                    })}
                  >
                    {f.probandName ??
                      t('routes.browseFamilies.table.unnamedProband', { defaultValue: 'Unnamed proband' })}
                  </span>
                )
              ) : (
                <span className="text-muted-foreground text-xs">{emptyCell}</span>
              )}
            </DataTableTd>
            <DataTableTd className="tabular-nums text-foreground/90" align="right">
              {f.memberCount.toLocaleString()}
            </DataTableTd>
            <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset align="right">
              {f.updatedAt !== null ? formatDate(f.updatedAt) : emptyCell}
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}
